import type { DealValues } from '../../api/registryDealTypes';
import type { SectionGroup, SetupField } from '../../domain/setup/fieldTypes';
import { ALL_FIELDS, SETUP_SECTIONS } from '../../domain/setup/sections';

export interface DefaultsBlock {
  title?: string;
  keys: string[];
  columns?: number;
}

export interface DefaultsSection {
  id: string;
  group: SectionGroup;
  title: string;
  description: string;
  blocks: DefaultsBlock[];
}

/**
 * The stored defaults shown on the live page: everything a new setup starts with that the setup
 * shows (findings 22 and 22+). Their order comes from the setup steps. BSB and account number are
 * never defaults, because each trust has its own bank account (finding 17).
 */
const DEFAULT_KEYS = new Set([
  'brand.website',
  'brand.loginUrl',
  'brand.colour',
  'brand.emailHeader',
  'brand.emailButton',
  'billing.entity',
  'directors',
  'directors.placeOfBusiness',
  'contact.email',
  'contact.phone',
  'contact.address',
  'contact.website',
  'privacy.url',
  'bank.name',
  'bank.apca',
  'bank.selfBalancing',
  'fund.entityType',
  'fund.trustee',
  'fund.currency',
  'tax.aiirType',
  'registrar.mode',
  'fund.strategy',
  'fund.basis',
  'fund.frequency',
  'class.partlyPaid',
  'class.paidOnApplication',
  'class.fee',
  'offer.min',
  'offer.max',
  'offer.increment',
  'class.name',
  'class.code',
  'class.category',
  'class.issuePrice',
  'class.valueRounding',
  'class.unitRounding',
  'class.priceRounding',
  'class.redRounding',
  'class.unitPrecision',
  'class.pricePrecision',
  'cert.enabled',
  'cert.startSeq',
  'cert.title',
  'cert.terms',
  'offer.timezone',
  'offer.closeTime',
  'offer.countdown',
  'offer.useExisting',
  'offer.issueAtClose',
  'offer.notesField',
  'offer.terms',
  'offer.ddReadIm',
  'offer.ddWholesale',
  'offer.ddRisks',
  'offer.branding',
  'offer.collectBank',
  'offer.payEft',
  'offer.payBpay',
  'offer.payDueMode',
  'offer.payDays',
  'comms.confirmEmail',
  'comms.confirmRelease',
  'comms.reminderRelease',
  'comms.scaleBackRelease',
  'comms.rejectedRelease',
  'comms.unitsIssuedRelease',
  'comms.certificateRelease',
  'comms.reminderDays',
  'comms.fromName',
  'notify.paymentReceived',
  'notify.dailySummary',
  'inv.wholesaleMode',
  'inv.verification',
  'inv.entityDocs',
  'inv.investorBankChange',
  'inv.staffBankChange',
  'inv.nameCase',
]);

/** The setup steps, blocks and field order, keeping only the fields that have a default. */
export const LIVE_DEFAULT_SECTIONS: DefaultsSection[] = SETUP_SECTIONS.map((section) => ({
  id: section.id,
  group: section.group,
  title: section.title,
  description: section.summary,
  blocks: section.blocks
    .map((block) => ({
      title: block.title,
      keys: block.fields.filter((field) => DEFAULT_KEYS.has(field.key)).map((field) => field.key),
      columns: block.columns,
    }))
    .filter((block) => block.keys.length > 0),
})).filter((section) => section.blocks.length > 0);

/** Setup wording that reads wrongly on the defaults page, where there is no deal yet. */
const DEFAULTS_FIELD_COPY: Record<string, Partial<Pick<SetupField, 'label' | 'help'>>> = {
  directors: { help: undefined },
  'fund.strategy': { help: 'The usual strategy. Each setup can still choose its own.' },
  'inv.entityDocs': { help: 'Always on: entities provide a trust deed or company constitution.' },
  'offer.closeTime': { label: 'Offer close time' },
};

/** A default is never required: required-ness belongs on the setup, checked at activation. */
export function asDefaultField(field: SetupField): SetupField {
  return { ...field, required: false, toConfirm: false, ...DEFAULTS_FIELD_COPY[field.key] };
}

export const isNotSet = (value: DealValues[string] | undefined) =>
  value === undefined || value === '';

export const sectionKeys = (section: DefaultsSection) =>
  section.blocks.flatMap((block) => block.keys);

/** Hides a field when the setup would, e.g. certificate details while certificates are off. */
export function isShown(key: string, values: DealValues) {
  const field = ALL_FIELDS.find((item) => item.key === key);
  return Boolean(field) && (!field!.showIf || field!.showIf(values));
}

export const shownKeys = (section: DefaultsSection, values: DealValues) =>
  sectionKeys(section).filter((key) => isShown(key, values));
